// The cart builder's "Add-on for a plan they have" section: a hosting add-on
// for one of the account's existing hosting plans, attached to it by domain.
//
// The plans come from the account's products (listAccountProducts →
// squish-backend lib/hosting-product-adapter.js); the add-ons from
// /api/products/live `hostingAddons` (squish-backend
// lib/hosting-addon-catalog.js). This side offers only what the catalog says
// fits the plan's kind; squish-backend prices the line (src/lib/cartQuote.js)
// and refuses what it can't attach (an add-on already on the plan, a bundle
// overlap, an add-on that needs a real domain on a placeholder site…), and
// that refusal is shown to the rep as written.
//
// The line names its plan in metadata.attachToDomain. That is what separates
// it from an add-on riding a NEW plan in the same cart, whose term follows
// that plan's line: isOwnedPlanAddon keeps the builder's in-cart add-on rules
// off it.

import { addonProductCode } from './productCodes.js';

// hosting_sites statuses that can never take a new charge (squish-backend
// lib/hosting-registry.js TERMINAL_STATUSES, upper-cased by the adapter).
export const PLAN_GONE_STATES = new Set(['CANCELLED', 'DELETED']);

// The catalog's appliesToCategories vocabulary: a Managed Cloud server is
// 'vps', a Web Hosting plan is 'shared'. A VPS row carries its server summary;
// an older row may have only the sku.
export function planCategory(row) {
  if (row?.server || /^hosting-managed-cloud-/.test(row?.sku || '')) return 'vps';
  return 'shared';
}

// The account's hosting plans an add-on can go on, each with the add-on skus
// already on it.
export function attachablePlans(products) {
  const list = Array.isArray(products) ? products : [];
  const attachedOn = new Map();
  for (const p of list) {
    if (p?.productKind !== 'hosting-addon' || PLAN_GONE_STATES.has(p.state) || !p.sku) continue;
    const parent = String(p.parentProductId || p.linkedDomain || '').trim().toLowerCase();
    if (!parent) continue;
    if (!attachedOn.has(parent)) attachedOn.set(parent, new Set());
    attachedOn.get(parent).add(p.sku);
  }
  const plans = [];
  const seen = new Set();
  for (const p of list) {
    if (p?.productKind !== 'hosting' || PLAN_GONE_STATES.has(p.state)) continue;
    const domain = String(p.productId || '').trim().toLowerCase();
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    plans.push({
      domain,
      sku: p.sku || null,
      label: p.label || null,
      state: p.state,
      category: planCategory(p),
      attached: attachedOn.get(domain) || new Set(),
    });
  }
  return plans.sort((a, b) => a.domain.localeCompare(b.domain));
}

// The add-ons the catalog sells for this plan: fits its kind, sold from a cart
// at all, and not already on it.
export function addonsForPlan(hostingAddonCatalog, plan) {
  if (!plan) return [];
  return Object.values(hostingAddonCatalog || {}).filter((a) => {
    if (!a?.sku || a.selfServe === false) return false;
    const cats = Array.isArray(a.appliesToCategories) ? a.appliesToCategories : [];
    if (!cats.includes(plan.category) && !cats.includes('*')) return false;
    return !plan.attached.has(a.sku);
  });
}

// One line per add-on per plan.
export const ownedAddonLineId = (domain, sku) => `ADDON_ON:${domain}:${sku}`;

// The line intent squish-backend prices (POST /api/carts/quote).
export function ownedAddonLine(plan, entry) {
  return {
    productCode: addonProductCode(entry.sku),
    productName: `${entry.label || entry.sku} — ${plan.domain}`,
    quantity: 1,
    metadata: { sku: entry.sku, attachToDomain: plan.domain },
  };
}

// An add-on for a plan the customer already has, rather than one riding a new
// plan in this cart.
export function isOwnedPlanAddon(item) {
  return typeof item?.metadata?.attachToDomain === 'string' && item.metadata.attachToDomain !== '';
}

// A hosting add-on riding a NEW plan line in this cart: the builder prices it
// from the catalog and its term follows that plan line's term. An owned-plan
// add-on is neither: squish-backend priced it, and its term is its own plan's.
export function ridesNewPlanLine(item) {
  return String(item?.productCode || '').startsWith('HOSTING_ADDON_') && !isOwnedPlanAddon(item);
}

// Why the rep can't add one right now (null = addable).
//   plans:  null while loading, { error } when the account's products failed,
//           otherwise the array from attachablePlans.
//   addons: addonsForPlan for the plan picked.
export function ownedAddonBlocker({ isInvoice, isProspect, catalog, plans, addons }) {
  if (isInvoice) return 'Card checkout only: switch to Card to sell an add-on';
  if (isProspect) return 'This is for a plan they already have, so it needs the customer\'s account';
  if (!catalog || Object.keys(catalog).length === 0) return 'Hosting add-ons catalog unavailable';
  if (plans == null) return 'Loading this account\'s plans…';
  if (plans.error) return `Couldn't load this account's plans: ${plans.error}`;
  if (plans.length === 0) return 'No hosting plan on this account. Sell a plan and its add-ons from the catalog.';
  if (!addons?.length) return 'Nothing more the catalog sells for this plan.';
  return null;
}
